import {useEffect} from 'react';
import {create} from 'zustand';

import {useAriaPageScope} from '../pageContext';
import {useRootTurnScope} from './scope.query';

export type ThreadProject = {entityName: string; projectName: string};

type ThreadProjectChoiceState = {
  /** pageProject is the page project the choice was made on. */
  pageProject: ThreadProject | null;
  chosen: ThreadProject | null;
  choose: (pageProject: ThreadProject | null, chosen: ThreadProject) => void;
};

/**
 * useThreadProjectChoice holds the project the viewer picked for the next new
 * thread. The choice lapses once the page shows a different project, so the
 * page's project is pre-selected again.
 */
export const useThreadProjectChoice = create<ThreadProjectChoiceState>(set => ({
  pageProject: null,
  chosen: null,
  choose: (pageProject, chosen) => set({pageProject, chosen}),
}));

function sameThreadProject(
  a: ThreadProject | null,
  b: ThreadProject | null
): boolean {
  return a?.entityName === b?.entityName && a?.projectName === b?.projectName;
}

/** usePageProject is the project the current page shows, if any. */
export function usePageProject(): ThreadProject | null {
  const {entityName, projectName} = useAriaPageScope();
  return entityName && projectName ? {entityName, projectName} : null;
}

/**
 * useNewThreadRootTurnScope resolves where the next new thread is created: the
 * project the viewer chose for it, else the page's project.
 */
export function useNewThreadRootTurnScope() {
  const pageProject = usePageProject();
  const chosenPageProject = useThreadProjectChoice(s => s.pageProject);
  const chosen = useThreadProjectChoice(s => s.chosen);
  const choiceLapsed =
    chosen != null && !sameThreadProject(chosenPageProject, pageProject);
  useEffect(() => {
    if (choiceLapsed) {
      useThreadProjectChoice.setState({pageProject: null, chosen: null});
    }
  }, [choiceLapsed]);
  const requested = chosen != null && !choiceLapsed ? chosen : pageProject;
  return useRootTurnScope(requested?.entityName, requested?.projectName);
}
